import { t } from "i18next";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Icons from "@app/assets/Icons";
import Loader from "@app/components/Loader";
import Pagination from "@app/components/Pagination";
import TableHeader from "@app/components/TableHeader";
import ViewUtilityHeader from "@app/components/ViewUtilityHeader";
import DownloadLinkModal from "@app/containers/downloadLinkModal";
import { RouteNames } from "@app/routes/index";
import {
    generateDownloadLink,
    getVersionControlData,
    initiateDownloadLink,
} from "@app/services/Endpoints/VersionControl.API";
import { HeadersMapInterface } from "@app/types/Common.types";
import { DownloadLinkData, VersionControlData } from "@app/types/VersionControl";
import { AppContext } from "@app/utils/contexts/App.context";
import { formatDate } from "@app/utils/timeHelpers";
import "./index.scss";

const sortByEnum = {
    DESC: 1,
    ASC: 0,
};

const ViewVersionManagement: FC = () => {
    const HeadersMap: HeadersMapInterface = {
        serialNo: {
            label: t("VIEW.VERSIONMANAGEMENT.SERIALNO"),
            layout: { lg: 1, sm: 1 },
            disableSort: true,
        },
        branchName: { label: t("VIEW.VERSIONMANAGEMENT.BRANCHNAME"), layout: { lg: 2, sm: 2 } },
        branchCode: { label: t("VIEW.VERSIONMANAGEMENT.ID"), layout: { lg: 1, sm: 1 } },
        installedVersion: {
            label: t("VIEW.VERSIONMANAGEMENT.INSTALLEDVERSION"),
            layout: { lg: 2, sm: 2 },
        },
        installedOn: { label: t("VIEW.VERSIONMANAGEMENT.INSTALLEDON"), layout: { lg: 2, sm: 2 } },
        upcomingVersion: {
            label: t("VIEW.VERSIONMANAGEMENT.NEXTVERSION"),
            layout: { lg: 2, sm: 2 },
        },
        status: { label: t("VIEW.VERSIONMANAGEMENT.STATUS"), layout: { lg: 1, sm: 1 } },
    };

    const [versionList, setVersionList] = useState<VersionControlData[]>([]);
    const [sortByObj, setSortByObj] = useState({
        sortBy: "installedOn",
        sortOrder: sortByEnum.DESC,
    });
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loader, setLoader] = useState(false);
    const [searchVal, setSearchVal] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [isScrollComing, setIsScrollComing] = useState(false);
    const { userPermissions } = useContext(AppContext);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [downloadLinkData, setDownloadLinkData] = useState<DownloadLinkData>();

    const navigate = useNavigate();
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        fetchVersionControlData();
    }, [pageNumber, pageSize, searchVal, sortByObj]);

    useEffect(() => {
        if (ref.current?.offsetWidth !== ref.current?.clientWidth) {
            setIsScrollComing(true);
        } else {
            setIsScrollComing(false);
        }
    }, [versionList]);

    const fetchVersionControlData = () => {
        setLoader(true);
        getVersionControlData(
            searchVal,
            pageNumber,
            pageSize,
            sortByObj.sortBy,
            sortByObj.sortOrder,
        )
            .then((res) => {
                setTotalPages(res?.data.totalPages ?? 0);
                setVersionList(res?.data?.data ?? []);
            })
            .catch((err) => console.error("Error fetching version control data:", err))
            .finally(() => setLoader(false));
    };

    const sortByHandler = (headerKey: string) => {
        setSortByObj((prev) => ({
            sortBy: headerKey,
            sortOrder: Number(!prev.sortOrder),
        }));
    };

    const onChangeRecordsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setPageNumber(1);
    };

    const onPageChange = (page: number) => setPageNumber(page);

    const handleSearch = (val: string) => {
        setSearchVal(val);
        setPageNumber(1);
    };

    const onEdit = (installationId: string) => {
        navigate(RouteNames.Edit, {
            state: { InstallationId: installationId },
        });
    };

    const redirectToAddBranchDeployment = () => {
        navigate(RouteNames.Add);
    };

    const handleOpenModal = () => {
        setShowModal(true);
        getDownloadLinkData();
    };

    const handleGenerateDownloadLink = () => {
        generateDownloadLink()
            .then((res) => {
                setDownloadLinkData(res?.data.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getDownloadLinkData = () => {
        initiateDownloadLink()
            .then((res) => {
                setDownloadLinkData(res?.data.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    return (
        <Container fluid className='version-control'>
            <div
                className={`version-control-page version-control--page ${
                    isScrollComing ? "version-control--isScroll" : ""
                }`}
                ref={ref}
            >
                <ViewUtilityHeader
                    onChange={onChangeRecordsPerPage}
                    placeholder={t("VERSION_MANAGEMENT.ADD.ADD_HEADING.PLACEHOLDER")}
                    btnLabel={t("VERSION_MANAGEMENT.ADD.ADD_HEADING.LABEL")}
                    secondaryBtnLabel={t("VERSION_MANAGEMENT.DOWNLOAD.BTN_HEADING.LABEL") as string}
                    onClick={
                        userPermissions && userPermissions["Create_Deployment Automation"]
                            ? redirectToAddBranchDeployment
                            : undefined
                    }
                    onSearch={handleSearch}
                    onSecondaryClick={handleOpenModal}
                />
            </div>
            <DownloadLinkModal
                showModal={showModal}
                setShowModal={setShowModal}
                handleGenerateDownloadLink={handleGenerateDownloadLink}
                downloadLinkData={downloadLinkData}
            />

            <TableHeader
                headersMap={HeadersMap}
                sortByHandler={sortByHandler}
                sortByObj={sortByObj}
                enableIconColumn
            />

            <div
                className={`version-control--page ${
                    isScrollComing ? "version-control--isScroll" : ""
                }`}
                id='listing-Outercontainer'
                ref={ref}
            >
                {loader ? (
                    <Loader data-testid='loader' />
                ) : versionList.length ? (
                    <div id='listing-Innercontainer'>
                        {versionList.map((item, index) => (
                            <Row
                                key={index}
                                className={`version-control__rowWrap ${
                                    index % 2 === 0 ? "version-control__rowGrey" : ""
                                }`}
                            >
                                <Col className='version-control--pl20' sm='1' lg='1'>
                                    {pageNumber === 1 && index < 9 ? "0" : ""}
                                    {(pageNumber - 1) * pageSize + (index + 1)}
                                </Col>
                                <Col sm='2' lg='2'>
                                    {item.branchName}
                                </Col>
                                <Col sm='1' lg='1'>
                                    {item.branchCode}
                                </Col>
                                <Col sm='2' lg='2'>
                                    {item.branchVersion}
                                </Col>
                                <Col sm='2' lg='2'>
                                    {item?.installedOn ? formatDate(item.installedOn) : ""}
                                </Col>
                                <Col sm='2' lg='2'>
                                    {item.upcomingVersion}
                                </Col>
                                <Col sm='1' lg='1'>
                                    <span
                                        className={`status-text ${item.upcommingVersionStatus?.toLowerCase()}`}
                                    >
                                        {item.upcommingVersionStatus}
                                    </span>
                                </Col>

                                <Col sm='1' lg='1'>
                                    {userPermissions &&
                                        userPermissions["Edit_Deployment Automation"] && (
                                            <img
                                                role='img'
                                                alt='edit'
                                                className='version-control__enablepointer'
                                                src={Icons.EditIcon}
                                                onClick={() => onEdit(item.installationId)}
                                            />
                                        )}
                                </Col>
                            </Row>
                        ))}
                    </div>
                ) : (
                    <div className='version-control--noData' data-testid='no-data'>
                        No Data Found
                    </div>
                )}
            </div>

            <Pagination
                onPageChange={onPageChange}
                totalPages={totalPages}
                pageSize={pageSize}
                pageNumber={pageNumber}
            />
        </Container>
    );
};

export default ViewVersionManagement;
